'use client';

import React from 'react';
import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

export const ROLES = {
  CLIENT: 'client',
  ADMIN: 'admin',
} as const;

type Role = (typeof ROLES)[keyof typeof ROLES];

// The impersonated CUSTOMER (the target being acted as)
interface ImpersonationTarget {
  firstName: string;
  lastName: string;
  email: string;
}

// The impersonating employee (the CSM/admin)
interface Impersonator {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface SessionWithoutActiveImpersonation {
  user_id?: number;
  impersonation?: false;
  impersonation_target?: never;
  // Admin-scoped sessions have no impersonation target but still know which
  // employee is behind the screen.
  impersonator?: Impersonator;
  role?: Role;
}

interface SessionWithActiveImpersonation {
  user_id?: number;
  impersonation: true;
  impersonation_target: ImpersonationTarget;
  impersonator?: Impersonator;
  role?: Role;
}

// Shared payload for impersonation-aware consumers. `user_id` may be missing
// when an upstream session omits it or normalization rejects an invalid value.
export type ImpersonationSessionState =
  | SessionWithoutActiveImpersonation
  | SessionWithActiveImpersonation;

const ImpersonationSessionContext = createContext<ImpersonationSessionState | null>(null);

interface ImpersonationSessionProviderProps {
  children: ReactNode;
  session: ImpersonationSessionState | null;
}

export function ImpersonationSessionProvider({
  children,
  session,
}: ImpersonationSessionProviderProps) {
  return (
    <ImpersonationSessionContext.Provider value={session}>
      {children}
    </ImpersonationSessionContext.Provider>
  );
}

export function useImpersonationSession() {
  return useContext(ImpersonationSessionContext);
}

function hasImpersonationTargetShape(
  value: unknown,
): value is SessionWithActiveImpersonation['impersonation_target'] {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof Reflect.get(value, 'firstName') === 'string' &&
    typeof Reflect.get(value, 'lastName') === 'string' &&
    typeof Reflect.get(value, 'email') === 'string'
  );
}

export function isImpersonatingSession(
  session: ImpersonationSessionState | null | undefined,
): session is SessionWithActiveImpersonation {
  return (
    session?.impersonation === true && hasImpersonationTargetShape(session.impersonation_target)
  );
}

/**
 * The one classification of "a Highlight employee is viewing the portal as a
 * customer": Better Auth impersonation (`impersonation === true`) or an
 * admin-scoped session (`role === 'admin'`). Deliberately separate from
 * `isImpersonatingSession`, which also picks the `useGetCurrentUser` fetch
 * strategy and so needs a real `impersonation_target`.
 */
export function isEmployeeActingAsCustomerSession(
  session: ImpersonationSessionState | null | undefined,
): boolean {
  return isImpersonatingSession(session) || session?.role === ROLES.ADMIN;
}
